import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Text,
  TextInput,
  PasswordInput,
  Paper,
  Group,
  Stack,
  Container,
  Title,
  Checkbox,
  Select,
} from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import { useForm } from "@mantine/form";
import { motion } from "framer-motion";
import {
  IconMail,
  IconLock,
  IconUser,
  IconPhone,
  IconArrowRight,
  IconCheck,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import {
  Colors,
  HttpErrorCodesObj,
  HttpSuccessCodesObj,
  ValidPasswordLength,
} from "../../Lib/data";
import { AllFatherContext } from "../../App";
import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  validateEmail,
} from "../../Lib/methods";
import { NonPaginatedResponse } from "../../Lib/responses";
import { getSiteName } from "../../Lib/siteContent";
import { LoginResponse } from "../../Lib/types";
import { PerformRequest } from "../../Lib/usePerformRequest";
import { countryCallingCodeOptions } from "../../Lib/countryCallingCodes";
import "./styles.scss";
import LanguageSelector from "../../Lib/i18n/LanguageSelector";

const Signup: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const signupForm = useForm({
    initialValues: {
      name: "",
      email: "",
      countryCode: "US",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      agreeToTerms: false,
    },
    validate: {
      name: (value) => (value.length < 3 ? t("auth.firstName") : null),
      email: (value) => (validateEmail(value) ? null : t("auth.email")),
      countryCode: (value) => (!value ? "Country code is required" : null),
      phoneNumber: (value) =>
        value.replace(/\D/g, "").length < 7 ? t("auth.phone") : null,
      password: (value) => (value.length < ValidPasswordLength ? t("auth.password") : null),
      confirmPassword: (value, values) =>
        value.length < ValidPasswordLength
          ? t("auth.confirmPassword")
          : value !== values.password
            ? t("auth.password")
            : null,
      agreeToTerms: (value) => (!value ? t("auth.termsOfServiceError") : null),
    },
  });

  const context = useContext(AllFatherContext);
  const siteName = getSiteName(context?.assets);
  const [isLoading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const SignupStudent = async () => {
    const { hasErrors } = signupForm.validate();
    if (!hasErrors) {
      setLoading(true);
      const selectedCountryCode =
        countryCallingCodeOptions.find((option) => option.value === signupForm.values.countryCode)
          ?.code ?? "+1";
      const r = await PerformRequest<NonPaginatedResponse<LoginResponse>>({
        method: Endpoints.GetStudentSignup().method,
        route: Endpoints.GetStudentSignup().route,
        data: {
          name: signupForm.values.name,
          email: signupForm.values.email,
          phone: `${selectedCountryCode}${signupForm.values.phoneNumber.replace(/\D/g, "")}`,
          password: signupForm.values.password,
        },
        callback: () => {
          setLoading(false);
        },
      });
      setLoading(false);
      if (r) {
        if (r.status === HttpSuccessCodesObj.Created) {
          localStorage.setItem("t", r?.data?.data?.token);
          DefaultSuccessNotification(t("auth.signUp"));
          navigate("/d/overview");
        } else if (r.status === HttpErrorCodesObj.NotFound) {
          DefaultErrorNotification(t("auth.signUp"));
        } else if (r.status === HttpErrorCodesObj.Conflict) {
          DefaultErrorNotification(t("auth.signUp"));
        } else if (r.status === HttpErrorCodesObj.Unauthorized) {
          DefaultErrorNotification(t("auth.signUp"));
        }
      } else {
        DefaultErrorNotification(t("auth.signUp"));
      }
    }
  };

  return (
    <div className="signup-page">
      <div style={{ position: "fixed", top: 24, right: 24, zIndex: 2000 }}>
        <LanguageSelector />
      </div>
      {/* Background Elements */}
      <div className="background-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
        <div className="shape shape-4"></div>
        <div className="shape shape-5"></div>
      </div>

      <Container size="sm" className="signup-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="signup-content"
        >
          {/* Logo Section */}
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="logo-section"
          >
            <img src={context?.assets?.logos?.legacy} alt={siteName} className="logo" />
            <Title order={1} className="app-title">
              {siteName}
            </Title>
            <Text className="welcome-text" size="lg" c="dimmed">
              {t("auth.createAccount")}
            </Text>
          </motion.div>

          {/* Signup Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Paper className="signup-form" radius="lg" p="xl" withBorder>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  SignupStudent();
                }}
              >
                <Stack gap="lg">
                  {/* Name Input */}
                  <TextInput
                    leftSection={<IconUser size={16} />}
                    label={t("auth.fullName")}
                    placeholder={t("auth.fullName")}
                    size="md"
                    radius="md"
                    variant="filled"
                    {...signupForm.getInputProps("name")}
                    classNames={{
                      input: "form-input",
                      label: "form-label",
                    }}
                  />

                  {/* Email Input */}
                  <TextInput
                    leftSection={<IconMail size={16} />}
                    label={t("auth.email")}
                    placeholder={t("auth.email")}
                    size="md"
                    radius="md"
                    variant="filled"
                    {...signupForm.getInputProps("email")}
                    classNames={{
                      input: "form-input",
                      label: "form-label",
                    }}
                  />

                  <Group wrap="nowrap" align="flex-start">
                    <Select
                      searchable
                      label="Country Code"
                      placeholder="Select code"
                      size="md"
                      radius="md"
                      variant="filled"
                      data={countryCallingCodeOptions}
                      nothingFoundMessage="No country code found"
                      {...signupForm.getInputProps("countryCode")}
                      style={{ flex: "0 0 180px", marginTop: "8px" }}
                    />

                    <TextInput
                      leftSection={<IconPhone size={16} />}
                      label="Phone"
                      placeholder={t("auth.phoneNumber")}
                      size="md"
                      radius="md"
                      type="number"
                      variant="filled"
                      {...signupForm.getInputProps("phoneNumber")}
                      style={{ flex: 1 }}
                      classNames={{
                        input: "form-input",
                        label: "form-label",
                      }}
                    />
                  </Group>

                  {/* Password Inputs */}
                  <div className="flex-row passwords align-center width-100 justify-between">
                    <PasswordInput
                      className="password"
                      leftSection={<IconLock size={16} />}
                      label={t("auth.password")}
                      placeholder={t("auth.password")}
                      size="md"
                      radius="md"
                      variant="filled"
                      visible={showPassword}
                      onVisibilityChange={setShowPassword}
                      {...signupForm.getInputProps("password")}
                      classNames={{
                        input: "form-input",
                        label: "form-label",
                        visibilityToggle: "password-toggle",
                      }}
                    />
                    <PasswordInput
                      className="password"
                      leftSection={<IconLock size={16} />}
                      label={t("auth.confirmPassword")}
                      placeholder={t("auth.confirmPassword")}
                      size="md"
                      radius="md"
                      variant="filled"
                      visible={showConfirmPassword}
                      onVisibilityChange={setShowConfirmPassword}
                      {...signupForm.getInputProps("confirmPassword")}
                      classNames={{
                        input: "form-input",
                        label: "form-label",
                        visibilityToggle: "password-toggle",
                      }}
                    />
                  </div>

                  {/* Terms and Conditions */}
                  <Checkbox
                    checked={signupForm.values.agreeToTerms}
                    onChange={(e) => signupForm.setFieldValue("agreeToTerms", e.target.checked)}
                    error={signupForm.errors.agreeToTerms}
                    label={
                      <Text size="sm" c="dimmed">
                        {t("auth.termsOfService")}{" "}
                        <Link to="/terms" className="terms-link">
                          {t("footer.termsOfService")}
                        </Link>{" "}
                        {t("common.and")}{" "}
                        <Link to="/privacy" className="terms-link">
                          {t("footer.privacyPolicy")}
                        </Link>
                      </Text>
                    }
                    classNames={{
                      label: "checkbox-label",
                    }}
                  />

                  {/* Signup Button */}
                  <Button
                    type="submit"
                    size="lg"
                    radius="md"
                    loading={isLoading}
                    className="signup-button"
                    rightSection={
                      isLoading ? <IconCheck size={16} /> : <IconArrowRight size={16} />
                    }
                  >
                    {isLoading ? t("auth.createAccount") : t("auth.createAccount")}
                  </Button>

                  {/* Login Link */}
                  <Group justify="center" className="login-link-group">
                    <Text size="sm" c="dimmed">
                      {t("auth.alreadyHaveAccount")}{" "}
                      <Link to="/login" className="login-link">
                        {t("auth.signIn")}
                      </Link>
                    </Text>
                  </Group>
                </Stack>
              </form>
            </Paper>
          </motion.div>
        </motion.div>
      </Container>
    </div>
  );
};

export default Signup;
